import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react";
import clsx from "clsx";

/**
 * LinkText コンポーネントのProps
 */
export interface LinkTextProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    /** リンクテキスト */
    children: ReactNode;
    /** バリアント */
    variant?: "primary" | "secondary" | "muted";
    /** サイズ */
    size?: "xs" | "sm" | "md" | "lg";
    /** 下線の表示 */
    underline?: "none" | "hover" | "always";
    /** 無効状態 */
    disabled?: boolean;
    /** 外部リンク（別タブで開く） */
    isExternal?: boolean;
    /** 左側アイコン */
    leftIcon?: ReactNode;
    /** 右側アイコン */
    rightIcon?: ReactNode;
}

/**
 * LinkText - リンクテキストコンポーネント
 *
 * @description
 * 「パスワードを忘れた場合」などのリンクテキスト用
 */
export const LinkText = forwardRef<HTMLAnchorElement, LinkTextProps>(
    (
        {
            children,
            variant = "primary",
            size = "md",
            underline = "hover",
            disabled = false,
            isExternal = false,
            leftIcon,
            rightIcon,
            className,
            href,
            onClick,
            ...props
        },
        ref
    ) => {
        // バリアントに応じたクラス
        const variantClasses = {
            primary: "text-brand-500 hover:text-brand-600",
            secondary: "text-gray-600 hover:text-gray-800",
            muted: "text-gray-500 hover:text-gray-600",
        };

        // サイズに応じたクラス
        const sizeClasses = {
            xs: "text-xs",
            sm: "text-sm",
            md: "text-base",
            lg: "text-lg",
        };

        // 下線に応じたクラス
        const underlineClasses = {
            none: "no-underline",
            hover: "no-underline hover:underline",
            always: "underline",
        };

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (disabled) {
                e.preventDefault();
                return;
            }
            onClick?.(e);
        };

        return (
            <a
                ref={ref}
                href={disabled ? undefined : href}
                onClick={handleClick}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                className={clsx(
                    "inline-flex items-center gap-1 transition-colors duration-200",
                    "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 rounded",
                    variantClasses[variant],
                    sizeClasses[size],
                    underlineClasses[underline],
                    disabled && "opacity-50 cursor-not-allowed pointer-events-none",
                    !disabled && "cursor-pointer",
                    className
                )}
                {...props}
            >
                {/* 左アイコン */}
                {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}

                {/* リンクテキスト */}
                <span>{children}</span>

                {/* 右アイコン */}
                {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}

                {/* 外部リンクアイコン */}
                {isExternal && !rightIcon && (
                    <ExternalLinkIcon className="w-3.5 h-3.5" />
                )}
            </a>
        );
    }
);

LinkText.displayName = "LinkText";

// 外部リンクアイコン
const ExternalLinkIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
        />
    </svg>
);

export default LinkText;
